import { useEffect, useMemo, useState } from "react";

import {
  createClient,
  createClientConnectionRecord,
  createClientRoom,
  createClientSector,
  createNetworkPoint,
  createPatchPanel,
  getClientStructureOverview,
  listClients
} from "../services/api";

const emptyOverview = {
  sectors: [],
  rooms: [],
  points: [],
  patch_panels: [],
  connections: []
};

const sampleConnection = {
  service_code: "CAM 15",
  trace_code: "IVO-CAM15-PT13-PP01-13",
  source_label: "ADM / SALA 01 / PT13",
  destination_label: "CPD / TI / PP01 / PT13",
  patch_panel_port: "13",
  connection_type: "CABO_UTP",
  media_type: "CABO_UTP_CAT6",
  status: "ACTIVE",
  notes: "Exemplo: CAM 15 - ADM/SALA 01/PT13 para CPD/TI/PP01 porta 13."
};

function Field({ label, children }) {
  return (
    <label>
      <span>{label}</span>
      {children}
    </label>
  );
}

function SimpleForm({ title, subtitle, children, onSubmit, loading }) {
  return (
    <form className="structure-form" onSubmit={onSubmit}>
      <header>
        <h3>{title}</h3>
        <small>{subtitle}</small>
      </header>
      {children}
      <button type="submit" className="primary" disabled={loading}>
        {loading ? "Salvando..." : "Cadastrar"}
      </button>
    </form>
  );
}

export function ClientStructurePage() {
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState("");
  const [overview, setOverview] = useState(emptyOverview);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [newClient, setNewClient] = useState({ name: "", document: "", service_address: "" });
  const [sector, setSector] = useState({ code: "ADM", name: "Administrativo", description: "" });
  const [room, setRoom] = useState({ sector_id: "", code: "SALA-01", name: "SALA 01", floor: "", description: "" });
  const [point, setPoint] = useState({ room_id: "", point_code: "PT13", trace_code: "IVO-PT13", service_code: "CAM 15", point_type: "CFTV", notes: "" });
  const [patchPanel, setPatchPanel] = useState({ code: "PP01", trace_code: "IVO-PP01", name: "Patch Panel 01", rack_name: "CPD / TI", port_count: 24 });
  const [connection, setConnection] = useState(sampleConnection);

  const selectedClient = useMemo(
    () => clients.find((client) => client.id === selectedClientId),
    [clients, selectedClientId]
  );

  async function refresh(clientId = selectedClientId) {
    setError("");
    try {
      const [clientData, structureData] = await Promise.all([
        listClients(),
        clientId ? getClientStructureOverview(clientId) : Promise.resolve(emptyOverview)
      ]);
      const items = clientData.items ?? [];
      setClients(items);
      if (!clientId && items[0]?.id) {
        setSelectedClientId(items[0].id);
        setOverview(await getClientStructureOverview(items[0].id));
      } else {
        setOverview(structureData);
      }
    } catch (err) {
      setError(err.message || "Nao foi possivel carregar o cadastro tecnico.");
    }
  }

  useEffect(() => {
    refresh();
  }, []);

  useEffect(() => {
    if (selectedClientId) {
      refresh(selectedClientId);
    }
  }, [selectedClientId]);

  async function submit(handler, successText) {
    if (!selectedClientId && handler !== createClient) {
      setError("Cadastre ou selecione um cliente antes.");
      return;
    }
    setLoading(true);
    setError("");
    setMessage("");
    try {
      const result = await handler();
      setMessage(successText);
      await refresh(result?.client_id ?? selectedClientId);
    } catch (err) {
      setError(err.message || "Nao foi possivel salvar.");
    } finally {
      setLoading(false);
    }
  }

  function clientPayload(payload) {
    return { ...payload, client_id: selectedClientId };
  }

  return (
    <section className="client-structure">
      <div className="page-heading">
        <div>
          <span className="eyebrow">Cadastro tecnico do cliente</span>
          <h1>Setores, salas, pontos, patch panel e ligações físicas/lógicas</h1>
          <p>
            Base de cadastro para transformar o levantamento de campo em memoria tecnica,
            rastreio por etiqueta/QR Code, topologia fisica, topologia logica, relatorio
            e futura inteligencia operacional.
          </p>
        </div>
      </div>

      {message ? <div className="login-success">{message}</div> : null}
      {error ? <div className="login-error">{error}</div> : null}

      <div className="structure-grid">
        <article className="panel structure-client-card">
          <h2>Cliente</h2>
          <Field label="Selecionar cliente">
            <select value={selectedClientId} onChange={(event) => setSelectedClientId(event.target.value)}>
              <option value="">Selecione...</option>
              {clients.map((client) => (
                <option key={client.id} value={client.id}>{client.name}</option>
              ))}
            </select>
          </Field>
          <div className="structure-selected-client">
            <strong>{selectedClient?.name ?? "Nenhum cliente selecionado"}</strong>
            <span>{selectedClient?.service_address ?? "Cadastre o cliente para liberar setores, salas e pontos."}</span>
          </div>
          <form
            className="structure-inline-form"
            onSubmit={(event) => {
              event.preventDefault();
              submit(async () => {
                const created = await createClient(newClient);
                setSelectedClientId(created.id);
                setNewClient({ name: "", document: "", service_address: "" });
                return created;
              }, "Cliente cadastrado.");
            }}
          >
            <Field label="Novo cliente">
              <input value={newClient.name} onChange={(event) => setNewClient({ ...newClient, name: event.target.value })} placeholder="IVO BARBOZA ADVOGADOS" required />
            </Field>
            <Field label="Documento">
              <input value={newClient.document} onChange={(event) => setNewClient({ ...newClient, document: event.target.value })} placeholder="CNPJ/CPF" />
            </Field>
            <Field label="Endereco">
              <input value={newClient.service_address} onChange={(event) => setNewClient({ ...newClient, service_address: event.target.value })} placeholder="Endereco de atendimento" />
            </Field>
            <button type="submit" className="secondary" disabled={loading}>Criar cliente</button>
          </form>
        </article>

        <article className="panel structure-summary">
          <h2>Resumo da base</h2>
          <div>
            <span>Setores</span><strong>{overview.sectors?.length ?? 0}</strong>
            <span>Salas</span><strong>{overview.rooms?.length ?? 0}</strong>
            <span>Pontos</span><strong>{overview.points?.length ?? 0}</strong>
            <span>Patch panels</span><strong>{overview.patch_panels?.length ?? 0}</strong>
            <span>DE→PARA</span><strong>{overview.connections?.length ?? 0}</strong>
          </div>
        </article>
      </div>

      <div className="structure-forms-grid">
        <SimpleForm
          title="1. Setor"
          subtitle="Ex.: ADM, FIN, CPD, TI, Patio, Anexo."
          loading={loading}
          onSubmit={(event) => {
            event.preventDefault();
            submit(() => createClientSector(clientPayload(sector)), "Setor cadastrado.");
          }}
        >
          <Field label="Codigo"><input value={sector.code} onChange={(event) => setSector({ ...sector, code: event.target.value })} required /></Field>
          <Field label="Nome"><input value={sector.name} onChange={(event) => setSector({ ...sector, name: event.target.value })} required /></Field>
          <Field label="Descricao"><input value={sector.description} onChange={(event) => setSector({ ...sector, description: event.target.value })} /></Field>
        </SimpleForm>

        <SimpleForm
          title="2. Sala / ambiente"
          subtitle="Sala, CPD, TI, rack, anexo ou ambiente tecnico."
          loading={loading}
          onSubmit={(event) => {
            event.preventDefault();
            submit(() => createClientRoom(clientPayload({ ...room, sector_id: room.sector_id || null })), "Sala cadastrada.");
          }}
        >
          <Field label="Setor">
            <select value={room.sector_id} onChange={(event) => setRoom({ ...room, sector_id: event.target.value })}>
              <option value="">Sem setor</option>
              {overview.sectors?.map((item) => <option key={item.id} value={item.id}>{item.code} - {item.name}</option>)}
            </select>
          </Field>
          <Field label="Codigo"><input value={room.code} onChange={(event) => setRoom({ ...room, code: event.target.value })} required /></Field>
          <Field label="Nome"><input value={room.name} onChange={(event) => setRoom({ ...room, name: event.target.value })} required /></Field>
          <Field label="Pavimento"><input value={room.floor} onChange={(event) => setRoom({ ...room, floor: event.target.value })} /></Field>
        </SimpleForm>

        <SimpleForm
          title="3. Ponto"
          subtitle="Ponto fisico em sala, camera, mesa, tomada logica ou equipamento."
          loading={loading}
          onSubmit={(event) => {
            event.preventDefault();
            submit(() => createNetworkPoint(clientPayload({ ...point, room_id: point.room_id || null })), "Ponto cadastrado.");
          }}
        >
          <Field label="Sala">
            <select value={point.room_id} onChange={(event) => setPoint({ ...point, room_id: event.target.value })}>
              <option value="">Sem sala</option>
              {overview.rooms?.map((item) => <option key={item.id} value={item.id}>{item.code} - {item.name}</option>)}
            </select>
          </Field>
          <Field label="Ponto"><input value={point.point_code} onChange={(event) => setPoint({ ...point, point_code: event.target.value })} required /></Field>
          <Field label="Codigo de rastreio"><input value={point.trace_code} onChange={(event) => setPoint({ ...point, trace_code: event.target.value })} placeholder="IVO-PT13, QR-PT13..." /></Field>
          <Field label="Servico / ativo"><input value={point.service_code} onChange={(event) => setPoint({ ...point, service_code: event.target.value })} placeholder="CAM 15, PC12..." /></Field>
          <Field label="Tipo"><input value={point.point_type} onChange={(event) => setPoint({ ...point, point_type: event.target.value })} /></Field>
        </SimpleForm>

        <SimpleForm
          title="4. Patch panel"
          subtitle="CPD, rack, patch panel e quantidade de portas."
          loading={loading}
          onSubmit={(event) => {
            event.preventDefault();
            submit(() => createPatchPanel(clientPayload(patchPanel)), "Patch panel cadastrado.");
          }}
        >
          <Field label="Codigo"><input value={patchPanel.code} onChange={(event) => setPatchPanel({ ...patchPanel, code: event.target.value })} required /></Field>
          <Field label="Codigo de rastreio"><input value={patchPanel.trace_code} onChange={(event) => setPatchPanel({ ...patchPanel, trace_code: event.target.value })} placeholder="IVO-PP01" /></Field>
          <Field label="Nome"><input value={patchPanel.name} onChange={(event) => setPatchPanel({ ...patchPanel, name: event.target.value })} required /></Field>
          <Field label="Rack / local"><input value={patchPanel.rack_name} onChange={(event) => setPatchPanel({ ...patchPanel, rack_name: event.target.value })} /></Field>
          <Field label="Portas"><input type="number" min="1" value={patchPanel.port_count} onChange={(event) => setPatchPanel({ ...patchPanel, port_count: Number(event.target.value) })} /></Field>
        </SimpleForm>
      </div>

      <article className="panel structure-connection-panel">
        <div className="panel-heading">
          <div>
            <span className="eyebrow">5. DE→PARA</span>
            <h2>Conectar ponto ao CPD / patch panel / rede logica</h2>
          </div>
        </div>
        <form
          className="structure-connection-form"
          onSubmit={(event) => {
            event.preventDefault();
            submit(() => createClientConnectionRecord(clientPayload({
              ...connection,
              source_point_id: connection.source_point_id || null,
              patch_panel_id: connection.patch_panel_id || null
            })), "DE→PARA cadastrado.");
          }}
        >
          <Field label="Servico / ativo"><input value={connection.service_code} onChange={(event) => setConnection({ ...connection, service_code: event.target.value })} placeholder="CAM 15" /></Field>
          <Field label="Codigo de rastreio"><input value={connection.trace_code ?? ""} onChange={(event) => setConnection({ ...connection, trace_code: event.target.value })} placeholder="IVO-CAM15-PT13-PP01-13" /></Field>
          <Field label="Ponto de origem">
            <select value={connection.source_point_id ?? ""} onChange={(event) => {
              const selected = overview.points?.find((item) => item.id === event.target.value);
              setConnection({
                ...connection,
                source_point_id: event.target.value,
                source_label: selected ? `${selected.service_code ?? ""} / ${selected.point_code}` : connection.source_label
              });
            }}>
              <option value="">Preencher manualmente</option>
              {overview.points?.map((item) => <option key={item.id} value={item.id}>{item.service_code ?? "Ponto"} - {item.point_code}</option>)}
            </select>
          </Field>
          <Field label="DE"><input value={connection.source_label} onChange={(event) => setConnection({ ...connection, source_label: event.target.value })} required /></Field>
          <Field label="Patch panel">
            <select value={connection.patch_panel_id ?? ""} onChange={(event) => {
              const selected = overview.patch_panels?.find((item) => item.id === event.target.value);
              setConnection({
                ...connection,
                patch_panel_id: event.target.value,
                destination_label: selected ? `${selected.rack_name ?? "CPD"} / ${selected.code}` : connection.destination_label
              });
            }}>
              <option value="">Preencher manualmente</option>
              {overview.patch_panels?.map((item) => <option key={item.id} value={item.id}>{item.code} - {item.name}</option>)}
            </select>
          </Field>
          <Field label="Porta PP"><input value={connection.patch_panel_port} onChange={(event) => setConnection({ ...connection, patch_panel_port: event.target.value })} /></Field>
          <Field label="PARA"><input value={connection.destination_label} onChange={(event) => setConnection({ ...connection, destination_label: event.target.value })} required /></Field>
          <Field label="Tipo">
            <select value={connection.connection_type} onChange={(event) => setConnection({ ...connection, connection_type: event.target.value })}>
              <option value="CABO_UTP">Fisica - cabo UTP</option>
              <option value="PATCH_CORD">Fisica - patch cord</option>
              <option value="FIBRA_SM">Fisica - fibra SM</option>
              <option value="FIBRA_MM">Fisica - fibra MM</option>
              <option value="ENERGIA">Fisica - energia</option>
              <option value="LOGICA">Logica - VLAN/IP/rede</option>
              <option value="LINK_WIRELESS">Logica/Fisica - wireless</option>
            </select>
          </Field>
          <Field label="Meio / rede"><input value={connection.media_type} onChange={(event) => setConnection({ ...connection, media_type: event.target.value })} placeholder="CABO_UTP_CAT6, VLAN250, SSID, fibra..." /></Field>
          <Field label="Observacoes"><input value={connection.notes} onChange={(event) => setConnection({ ...connection, notes: event.target.value })} /></Field>
          <button type="submit" className="primary" disabled={loading}>Cadastrar DE→PARA</button>
        </form>
      </article>

      <article className="panel">
        <h2>Registros DE→PARA do cliente</h2>
        <div className="structure-table">
          <div className="structure-table-head">
              <span>Rastreio</span><span>Servico</span><span>DE</span><span>PARA</span><span>Meio</span><span>Status</span>
          </div>
          {overview.connections?.length ? overview.connections.map((item) => (
            <div key={item.id}>
              <span>{item.trace_code ?? "-"}</span>
              <span>{item.service_code ?? "-"}</span>
              <span>{item.source_label}</span>
              <span>{item.destination_label}{item.patch_panel_port ? ` / porta ${item.patch_panel_port}` : ""}</span>
              <span>{item.media_type}</span>
              <span>{item.status}</span>
            </div>
          )) : <p className="empty-state">Nenhuma conexao cadastrada para este cliente.</p>}
        </div>
      </article>
    </section>
  );
}
